import { useTranslation } from 'react-i18next';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Pin, PinOff } from 'lucide-react';
import { RiEqualizerLine, RiExpandVerticalLine, RiCollapseVerticalLine, RiArrowLeftRightLine, RiArrowRightUpLine } from '@remixicon/react';
import Listbox from '../../components/Listbox';
import PageShell from '../../components/PageShell';
import CardShell from '../../components/CardShell';
import FilterSearch from '../../components/FilterSearch';
import PillFilter from '../../components/PillFilter';
import SegmentedToggle from '../../components/SegmentedToggle';
import { ReportingContext } from '../../components/ReportingControls';
import { useViewExcelExport } from '../../components/ExcelExportButton';
import { useRegisterPageExports } from '../../exports/pageExports';
import { useP } from '../../i18n/useP';
import { formatNumber } from '../../utils/format';
import { usePersistentValue, usePersistentValueLocal } from '../../hooks/usePersistentState';
import {
  getOfferMonths, setForecast, setForecastMonth, setForecastRule,
  addForecastLine, renameForecastLine, deleteForecastLine,
  listSeasonProfiles, createSeasonProfile, updateSeasonProfile, deleteSeasonProfile, loadStandardProfiles,
} from '../../api/salesOfferMonths';
import { listOffers, updateOffer, listOfferModels } from '../../api/salesOffers';
import { getPlRevenueView } from '../../api/financials_pl';
import OfferCreateDialog from './OfferCreateDialog';
import { Cell, Choix } from '../../components/InlineCells';
import { OfferIcon, iconOf } from './offerIcons';
import { libelleUnite, HEURES_JOUR, lectureTemps } from './offerUnits';
import { OfferYearCard } from './OfferMonthSummary';
import SeasonProfilesPanel from './SeasonProfilesPanel';
import './OfferMonthPage.css';

/* ── Prévisions des ventes et Synthèse (décisions d'Olivier, 2026-10-10 ;
   protos `WIP/protos/previsions-vendeur.html` et `synthese-comparaison.html`,
   brief `WIP/briefs/ventes-previsions-vendeur.md`).

   PRÉVISIONS DES VENTES, deux ÉTAPES :
     Année            un seul écran où TOUT se tape, dans l'unité de l'offre
                      (pièces, heures ; francs pour un montant planifié) : le
                      réalisé, le portefeuille, puis les nouvelles ventes OU le
                      total annuel. Celui qu'on tape tient (bleu), l'autre
                      s'adapte (noir, toujours modifiable).
     Mensualisation   répartit l'année sur les mois ouverts, sans jamais la
                      changer : une règle par offre (linéaire, montée en
                      charge, un profil nommé), qu'une ligne remplace flux par
                      flux ; un mois tapé (bleu) garde sa valeur, les autres
                      (noir) se partagent le reste ; « À répartir » dit ce qui
                      ne se répartit pas. Les profils se gèrent dans la carte
                      du dessous.
   Dessous, l'impact financier en francs ; un clic sur une offre le détaille
   ligne par ligne.

   La CIBLE est figée sur la version de plan (Paramètres › Planification) ;
   « Comparer à » (le choix de chacun) ajoute un autre plan et son écart. Les
   lignes des prévisions sont les leurs, détachées de la Trajectoire. */

const MOIS = (p) => [p('Jan'), p('Fév'), p('Mar'), p('Avr'), p('Mai'), p('Juin'),
                     p('Juil'), p('Août'), p('Sep'), p('Oct'), p('Nov'), p('Déc')];
const MOIS_LONGS = (p) => [p('janvier'), p('février'), p('mars'), p('avril'), p('mai'), p('juin'),
                           p('juillet'), p('août'), p('septembre'), p('octobre'), p('novembre'), p('décembre')];

export default function OfferMonthPage({ activeTenant, year, addToast, canWrite = false, section = 'synthese', onGoRealise = null }) {
  const { t } = useTranslation();
  const p = useP();
  const prevision = section === 'portefeuille';
  const titre = prevision ? t('nav:items.sales-backlog') : p('Synthèse');
  const tid = activeTenant?.id;
  const rep = useContext(ReportingContext);
  /* UNE SEULE PAGE (décision d'Olivier, 2026-10-11, proto
     `WIP/protos/previsions-une-page.html`) : le chiffre d'affaires, à l'année ou
     par mois ; les ventes qui se tapent ; les mois, ouverts pour retoucher. La
     COMPARAISON et ces choix se retiennent pour la session, par client. */
  /* DEUX ÉCRANS dans les Prévisions (décision d'Olivier, 2026-10-11) : le
     CHIFFRE D'AFFAIRES, où le CA réalisé se tape, et les VENTES, ce qui reste à
     vendre ; une bascule sur la rangée des Profils. */
  const [ecran, setEcran] = usePersistentValue(`om_ecran_${tid || 'none'}`, 'ca');        // ca · ventes
  const [caVue, setCaVue] = usePersistentValue(`om_cavue_${tid || 'none'}`, 'an');          // an · mois
  const [cmp, setCmp] = usePersistentValue(`om_cmp_${tid || 'none'}`, '');
  /* Le RÉALISÉ se tape en quantité ou en CA (2026-10-10) ; la MENSUALISATION
     bascule sur les profils, et se filtre par offre et par flux. */
  const [modeReel, setModeReel] = usePersistentValue(`om_reel_${tid || 'none'}`, 'qty');   // qty · chf
  const [profilsOuverts, setProfilsOuverts] = usePersistentValue(`om_profils_${tid || 'none'}`, false);
  /* L'ATTERRISSAGE SE LIBÈRE COMME LE MENU (demande d'Olivier, 2026-10-11) :
     une punaise le garde dans sa colonne ou le libère ; libéré, les ventes
     prennent toute la largeur, une languette garde l'écart en vue, et le
     panneau revient en FLOTTANT au survol du bord droit. Le choix reste dans
     le navigateur. */
  const [atterrissageLibre, setAtterrissageLibre] = usePersistentValueLocal('om_atterrissage_libre', false);
  const [atterrissageVu, setAtterrissageVu] = useState(false);
  const [filtreRech, setFiltreRech] = useState(new Set());     // « o:<offre> » ou « l:<ligne> »
  /* L'AFFICHAGE de la mensualisation, deux interrupteurs sur la rangée de la page
     (demande d'Olivier, 2026-10-20, à la place de « Par flux / Par offre », que
     les clients confondaient) : le portefeuille seul, les nouvelles ventes seules,
     ou LES DEUX, chaque offre dépliable sur ses deux flux (ex-« Par offre »). Un
     clic sur une offre replie ou ouvre ses lignes (2026-10-10). */
  const [affichage, setAffichage] = usePersistentValue(`om_affichage_${tid || 'none'}`, 'both');   // bk · new · both
  const axe = affichage === 'both' ? 'offre' : 'flux';
  const [fluxOuverts, setFluxOuverts] = useState(new Set());   // Par offre : lignes ouvertes sur leurs deux flux
  const [caOuvertes, setCaOuvertes] = useState(new Set());   // Année : offres dépliées dans la carte du CA
  const [view, setView] = useState(null);
  const [profils, setProfils] = useState([]);
  const [unit, setUnit] = useState('kchf');
  const pageUnit = useMemo(() => ({ value: unit, onChange: setUnit }), [unit, setUnit]);
  const [edition, setEdition] = useState(null);         // la fenêtre d'une offre
  const [ouvertes, setOuvertes] = useState({ ca: new Set(), fac: new Set() });   // Synthèse : offres dépliées
  const [caReel, setCaReel] = useState(null);
  /* La REPRISE D'ACOMPTES CLIENTS du compte de résultat (demande d'Olivier,
     2026-10-12) : du CA déjà payé, réglé dans Finance (Fonds de roulement ›
     Acomptes clients). Elle se lit ici pour que le total des Prévisions soit
     celui du compte de résultat. */
  const [reprise, setReprise] = useState(null);
  /* Du TEMPS se lit en heures ou en jours-hommes, au choix, par offre. */
  const [unitesAff, setUnitesAff] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem(`om_unites_${tid || 'none'}`) || '{}'); } catch (e) { return {}; }
  });
  const exportRef = useRef(null);

  /* Les plans qu'on peut comparer : tous, sauf celui qu'on lit. */
  const versions = useMemo(() => (rep && rep.versions) || [], [rep]);
  const lue = (rep && rep.versionId) || (versions.find((v) => v.is_current) || {}).id || null;
  const autres = useMemo(() => versions.filter((v) => v.id !== lue), [versions, lue]);
  /* « Comparer à » lit la TRAJECTOIRE ou les PRÉVISIONS d'un plan (décision
     d'Olivier, 2026-10-10) ; un choix ancien, l'identifiant seul, en lisait les
     Prévisions. */
  const optionsCmp = useMemo(() => [{ id: '', label: p('Aucun plan') },
    ...autres.map((v) => ({ id: `forecast:${v.id}`, label: `${p('Prévisions')} · ${v.label}` })),
    ...versions.map((v) => ({ id: `trajectory:${v.id}`, label: `${p('Trajectoire')} · ${v.label}` }))], [autres, versions, p]);
  const cmpNorm = cmp && !String(cmp).includes(':') ? `forecast:${cmp}` : cmp;
  const compare = optionsCmp.some((o) => o.id && o.id === cmpNorm) ? cmpNorm : '';
  /* Le nom d'un plan lu : la Trajectoire de la version ouverte, ou la
     Trajectoire ou les Prévisions d'un autre plan. */
  const nomPlan = (t) => (!t ? '' : !t.version_id ? p('Trajectoire')
    : `${t.kind === 'trajectory' ? p('Trajectoire') : p('Prévisions')} · ${t.label}`);

  const reload = useCallback(async () => {
    if (!tid || !year) return;
    const [r, pr] = await Promise.all([getOfferMonths(tid, year, 1, compare || null), listSeasonProfiles(tid)]);
    if (r.error) { addToast && addToast(r.error, 'error'); return; }
    setView(r.data);
    setProfils((pr && pr.data) || []);
  }, [tid, year, compare, addToast]);
  useEffect(() => { reload(); }, [reload]);

  /* Le CA réalisé de la comptabilité, quand Finance répond : la Synthèse s'y
     aligne, les Prévisions en lisent l'écart (2026-10-10). */
  useEffect(() => {
    if (!tid || !year) return undefined;
    let vivant = true;
    getPlRevenueView(tid, year)
      .then((r) => {
        const rows = (r && r.data && r.data.rows) || [];
        const tot = rows.find((x) => x.type === 'section_total' && x.section === 'revenue');
        const lignes = (r && r.data && r.data.lines) || [];
        const ligne = lignes.find((x) => x.line_role === 'advances_release');
        const rr = ligne ? rows.find((x) => x.type === 'detail' && x.code === ligne.id) : null;
        if (vivant) {
          setCaReel(tot && tot.total_ytd != null ? Number(tot.total_ytd) : null);
          setReprise(rr && rr.total != null ? Number(rr.total) : null);
        }
      })
      .catch(() => { if (vivant) { setCaReel(null); setReprise(null); } });
    return () => { vivant = false; };
  }, [tid, year]);

  const excel = useViewExcelExport({ ref: exportRef, sheetName: titre, fileBase: titre, year, addToast });
  const excelRun = useRef(null); excelRun.current = excel.run;
  const exportOptions = useMemo(() => [{ id: 'excel', label: 'Excel', run: () => excelRun.current && excelRun.current() }], []);
  useRegisterPageExports(exportOptions);

  /* Les commandes de PAGE (étage 3) : l'écran, les profils, puis la
     comparaison, qui ne vaut que pour le chiffre d'affaires. */
  const vuesPage = useMemo(() => {
    const cmpCtl = { id: 'cmp', kind: 'select', label: p('Comparer à'), value: compare, onChange: setCmp, options: optionsCmp };
    if (!prevision) return [cmpCtl];
    /* LA SAISIE ET L'AFFICHAGE vivent sur la rangée de la page, à côté de la vue
       (demande d'Olivier, 2026-10-20, qui remplace les deux lignes de boutons
       radio) : Volumes ou Valeur ; puis Portefeuille et Nouvelles ventes, deux
       interrupteurs dont un reste toujours allumé. Les deux allumés = les deux
       flux, chaque offre dépliable ; un seul = ce flux, ses offres et ses lignes. */
    const surVentes = ecran === 'ventes' && !profilsOuverts && view && view.rows.length > 0;
    const avecSaisie = surVentes && view.rows.some((r) => r.mode !== 'amount');
    const retenus = new Set(affichage === 'both' ? ['bk', 'new'] : [affichage]);
    return [{ id: 'ecran', kind: 'segmented', label: p('Vue'), value: profilsOuverts ? null : ecran,
              onChange: (v) => { setEcran(v); setProfilsOuverts(false); },
              options: [{ id: 'ventes', label: p('Prévision des ventes') }, { id: 'ca', label: p("Impact chiffre d'affaires") }] },
            ...(avecSaisie ? [{ id: 'saisie', kind: 'segmented', label: p('Saisie'), value: modeReel, onChange: setModeReel,
                                options: [{ id: 'qty', label: p('Volumes') }, { id: 'chf', label: p('Valeur') }] }] : []),
            ...(surVentes ? [{ id: 'affichage', kind: 'segmented', multiple: true, label: p('Affichage'), value: retenus,
                              onChange: (set) => {
                                if (set.size === 0) return;                    // un flux au moins reste allumé
                                setAffichage(set.size === 2 ? 'both' : [...set][0]);
                              },
                              options: [{ id: 'bk', label: p('Portefeuille') }, { id: 'new', label: p('Nouvelles ventes') }] }] : []),
            ...(ecran === 'ca' && !profilsOuverts ? [cmpCtl] : [])];
  }, [p, prevision, ecran, compare, optionsCmp, profilsOuverts, view, affichage, modeReel]);   // eslint-disable-line

  const appel = async (promesse) => {
    const r = await promesse;
    if (r && r.error) addToast && addToast(r.error, 'error');
    await reload();
  };
  const ouvrirOffre = async (r) => {
    const [o, m, pl] = await Promise.all([listOffers(tid), listOfferModels(tid), getPlRevenueView(tid, year)]);
    if (o.error || m.error) { addToast && addToast(o.error || m.error, 'error'); return; }
    const offre = (o.data || []).find((x) => x.id === r.offer_id);
    if (!offre) return;
    const rows = pl?.data?.rows || [];
    const seen = new Set();
    const lines = rows.filter((x) => x.type === 'detail' && x.section === 'revenue' && !seen.has(x.label) && seen.add(x.label))
      .map((x) => ({ value: x.code, label: x.label }));
    setEdition({ offre, modeles: m.data || [], lines });
  };
  const modifierOffre = async (champs) => {
    const r = await updateOffer(tid, edition.offre.id, champs);
    if (r.error) { addToast && addToast(r.error, 'error'); return; }
    setEdition(null);
    await reload();
  };

  const vue = view;
  const an0 = String((vue?.years || [year])[0]);
  const dev = unit === 'kchf' ? 'kCHF' : 'CHF';
  const ouverts = useMemo(() => (vue?.open || []).filter((i) => i < 12), [vue]);
  const moisClos = ouverts.length < 12;
  const finClos = moisClos && ouverts.length ? MOIS_LONGS(p)[ouverts[0] - 1] : (moisClos ? MOIS_LONGS(p)[11] : null);
  const cols = useMemo(() => { const noms = MOIS(p); return ouverts.map((i, k) => ({ i, label: noms[i], sep: k === 0 })); }, [ouverts, p]);
  /* LES DOUZE MOIS, TOUJOURS (demande d'Olivier, 2026-10-20) dans la table des
     ventes, comme un exercice entièrement à venir : les mois clos s'y lisent
     GRISÉS, pour mémoire (la photo du plan à la clôture), sans rien compter ni
     se taper, sous un bandeau RÉALISÉ ; les mois à venir se tapent, sous un
     bandeau PRÉVISIONNEL. */
  const colsV = useMemo(() => {
    const noms = MOIS(p);
    return Array.from({ length: 12 }, (_, i) => ({ i, label: noms[i], clos: !ouverts.includes(i), sep: moisClos && i === ouverts[0] }));
  }, [ouverts, p, moisClos]);
  const nbClos = colsV.filter((c) => c.clos).length;

  /* ── Les nombres ──────────────────────────────────────────────────────── */
  const NA = <span className="om-na">{p('n/a')}</span>;
  const nb = (v) => (v == null ? '' : formatNumber(unit === 'kchf' ? v / 1000 : v, 0));
  const val = (v) => (v == null || Math.abs(v) < (unit === 'kchf' ? 500 : 0.5) ? '–' : nb(v));
  const brut = (v) => (v == null ? null : unit === 'kchf' ? v * 1000 : v);
  const chf = (x) => (x == null ? '' : String(Math.round(unit === 'kchf' ? x / 1000 : x)));
  const qteF = (v) => {
    if (v == null) return '';
    if (Math.abs(v) < 0.005) return '–';
    const r = Math.round(v * 100) / 100;
    return formatNumber(r, Number.isInteger(r) ? 0 : Math.round(r * 10) / 10 === r ? 1 : 2);
  };
  const tempsDe = (o) => o.equation === 'time_rate' && ['h', 'jh'].includes(o.unit);
  const affDe = (o) => (tempsDe(o) ? (unitesAff[o.offer_id] || o.unit) : o.unit);
  const kDe = (o) => (!tempsDe(o) || affDe(o) === o.unit ? 1 : o.unit === 'h' ? 1 / HEURES_JOUR : HEURES_JOUR);
  const basculerUnite = (o) => setUnitesAff((x) => {
    const n = { ...x, [o.offer_id]: affDe(o) === 'h' ? 'jh' : 'h' };
    try { window.localStorage.setItem(`om_unites_${tid || 'none'}`, JSON.stringify(n)); } catch (e) { /* sans mémoire, tant pis */ }
    return n;
  });
  /* Ce qu'une offre affiche et ce qu'elle stocke : ses unités (converties en
     jours-hommes si on le demande), ou des francs pour un montant planifié. */
  const mesure = (o) => {
    if (o.mode === 'amount') return { fmt: val, draft: chf, brute: brut, aff: (x) => x };
    return { fmt: qteF, ...lectureTemps(kDe(o)) };
  };
  const unitePx = (r) => (r.mode === 'amount' ? dev : r.unit ? libelleUnite(p, affDe(r)) : p('unités'));
  /* L'UNITÉ DU RANG EN PUCE (demande d'Olivier, 2026-10-20) : le driver (heure,
     jour-homme, unité) et la devise se lisaient à peine, en petit gris à côté du
     nom. Une puce à filet les montre ; celle du temps est un BOUTON (une flèche
     le dit) qui passe des heures aux jours-hommes. */
  const puceUnite = (txt) => <span className="om-unit">{txt}</span>;
  const etiquetteUnite = (r) => (tempsDe(r)
    ? <button type="button" className="om-unit om-unit--sw" title={p('Lire en heures ou en jours-hommes')}
              onClick={(e) => { e.stopPropagation(); basculerUnite(r); }}>
        {libelleUnite(p, affDe(r))}<RiArrowLeftRightLine aria-hidden="true" />
      </button>
    : puceUnite(unitePx(r)));
  /* Le PRIX UNITAIRE se tape dans sa colonne (décision d'Olivier, 2026-10-10) :
     la Trajectoire étant détachée, elle ne le fournit plus. Il reste en
     francs quelle que soit la bascule kCHF, par unité AFFICHÉE (heure ou
     jour-homme) ; tapé, il tient (bleu), repris de l'année d'avant ou du prix
     indicatif de l'offre, il se lit en noir. */
  /* LES PREMIÈRES COLONNES RESTENT EN PLACE (demande d'Olivier, 2026-10-11) :
     l'offre, le prix (par offre), la règle puis le total planifié ; les mois
     défilent dessous. Standard `.lk-stick` : `--stick` porte le décalage. */
  // Par flux, les offres s'alignent sur le nom du bloc, sans gouttière : la
  // colonne se resserre d'autant (demande d'Olivier, 2026-10-11).
  /* Le prix et le coût unitaires se suivent sous UN entête (2026-10-20, demande d'Olivier) :
     deux colonnes étroites au lieu de deux colonnes larges, pour gagner de la place. */
  const LARG = { lbl: axe === 'flux' ? 206 : 236, prix: 70, cout: 62, regle: 75, an: 96 };
  /* LE PRIX UNITAIRE sur les deux lectures (demande d'Olivier, 2026-10-12) :
     par flux, il manquait. C'est celui de la ligne, il se tape ici comme là. */
  const ordreGel = ['lbl', 'prix', 'cout', 'regle', 'an'];
  const NOGEL = { c: '', s: undefined };
  const G = (k) => {
    const i = ordreGel.indexOf(k);
    if (i < 0) return NOGEL;
    const gauche = ordreGel.slice(0, i).reduce((a, x) => a + LARG[x], 0);
    return { c: ` lk-stick${k === 'an' ? ' lk-stick--last' : ''}`, s: { '--stick': `${gauche}px` } };
  };
  const fmtPrix = (x) => (x == null ? '' : formatNumber(x, Number.isInteger(Math.round(x * 100) / 100) ? 0 : 2));
  /* LE COÛT des produits vendus (demande d'Olivier, 2026-10-12) : il se tape sur
     la ligne des Prévisions, en francs par unité pour une offre à quantités, en
     pour cent du chiffre d'affaires pour un montant planifié. Il ne vient plus de la
     Trajectoire : vide, il vaut zéro. */
  const celluleCout = (r, l, g = NOGEL) => {
    const pct = r.mode === 'amount';
    const k = pct ? 1 : kDe(r);
    const v = pct ? (l.cost_pct || {})[an0] : (l.unit_cost || {})[an0];
    const tape = v != null;
    return (
      <td className={`lk-num${tape ? '' : ' om-der'}${g.c}`} style={g.s}>
        <Cell value={v == null ? null : Math.round((v / k) * 100) / 100} type="number" canWrite={canWrite}
              format={(x) => (x == null ? '' : `${fmtPrix(x)}${pct ? ' %' : ''}`)} placeholder="–" calc
              onSave={(x) => ecrireAn(r, l, pct ? 'cost_pct' : 'cost', x == null ? null : Math.round(x * k * 100) / 100)} />
      </td>
    );
  };
  const cellulePrix = (r, l, g = NOGEL) => {
    if (r.mode === 'amount') return <td className={`lk-num${g.c}`} style={g.s} />;
    const k = kDe(r);
    const prix = (l.price || {})[an0];
    const tape = (l.price_source || {})[an0] === 'line';
    return (
      <td className={`lk-num${tape ? '' : ' om-der'}${g.c}`} style={g.s}>
        <Cell value={prix == null ? null : Math.round((prix / k) * 100) / 100} type="number" canWrite={canWrite}
              format={fmtPrix} placeholder="–"
              onSave={(x) => ecrireAn(r, l, 'price', x == null ? null : Math.round(x * k * 100) / 100)} />
      </td>
    );
  };

  /* ── Les valeurs annuelles d'une ligne, ou d'une offre (somme de ses lignes) ── */
  const annee = (l) => (l.forecast || {})[an0] || {};
  /* LA TABLE DE L'ANNÉE SE LIT EN VOLUMES OU EN CA (option 2, 2026-10-10) :
     en CA, toutes ses colonnes sont en francs et ce qu'on y tape se garde tel
     quel, les unités s'en déduisant au prix de la ligne. Une ligne au montant
     est toujours en francs. `forme` dit dans quelle grandeur une valeur a été
     tapée : bleue dans la sienne, noire dans l'autre. */
  const enCA = (r) => modeReel === 'chf' && r.mode !== 'amount';
  const lu = (r, l) => {
    const fc = annee(l);
    const ca = enCA(r);
    return { bk: (ca ? fc.backlog_value : fc.backlog) ?? 0, neuf: (ca ? fc.new_value : fc.new) ?? 0,
             total: (ca ? fc.total_value : fc.total) ?? 0 };
  };

  /* ── Écritures ──────────────────────────────────────────────────────── */
  const ecrireAn = (r, l, champ, x, enFrancs = false) => appel(setForecast(tid, r.offer_id, Number(an0),
    { field: champ, value: x, line_id: l.line_id || undefined, ...(enFrancs ? { in_chf: true } : {}) }));
  const ecrireMois = (r, l, flux, i, x, enFrancs = false) => appel(setForecastMonth(tid, r.offer_id, Number(an0), i + 1,
    { flux, value: x, line_id: l.line_id || undefined, ...(enFrancs ? { in_chf: true } : {}) }));
  const poserRegle = (r, l, flux, regle) => appel(setForecastRule(tid, r.offer_id,
    flux ? { rule: regle, flux, line_id: l && l.line_id ? l.line_id : undefined, year: Number(an0) }
      : { rule: regle, year: Number(an0) }));
  const ajouterLigne = (r) => appel(addForecastLine(tid, r.offer_id, p('Nouvelle ligne')));
  const renommer = (l, label) => appel(renameForecastLine(tid, l.line_id, label));
  const supprimer = (l) => {
    if (window.confirm(p('Supprimer cette ligne et tout ce qu’elle porte, réalisé compris ?'))) appel(deleteForecastLine(tid, l.line_id));
  };

  /* ── Étape 1 : l'ANNÉE ──────────────────────────────────────────────── */
  /* DEUX CARTES (décision d'Olivier, 2026-10-10, proto
     `WIP/protos/previsions-valeur-et-ca.html`) : les VENTES, ce qui se tape, en
     volumes ou en valeur vendue ; puis le CHIFFRE D'AFFAIRES que ces ventes
     donnent, contre la cible de la Trajectoire (du CA, des volumes livrés),
     l'écart avec la comptabilité et le report. Un carnet vendu en décembre
     n'est pas un carnet reconnu : la cible ne se lit donc pas dans les ventes. */
  const avecCmp = Boolean(vue && vue.compare);
  const nomCible = vue && vue.target ? nomPlan(vue.target) : p('Trajectoire');
  const mesureCHF = { fmt: val, draft: chf, brute: brut, aff: (x) => x };
  const mesureAn = (r) => (enCA(r) ? mesureCHF : mesure(r));
  const celluleAn = (r, l, champ, valeur, { bleu = true, cls = '', ro = false, g = NOGEL } = {}) => {
    const m = mesureAn(r);
    const ca = enCA(r);
    // En MANUEL, l'année est la somme des mois (2026-10-11) : elle se lit.
    if (ro) return <td className={`lk-num om-der${cls}${g.c}`} style={g.s}>{valeur == null ? '–' : (m.fmt(m.aff(valeur)) || '–')}</td>;
    return (
      <td className={`lk-num${bleu ? '' : ' om-der'}${cls}${g.c}`} style={g.s}>
        <Cell value={m.aff(valeur)} type="number" canWrite={canWrite} format={(x) => (x == null ? '' : m.fmt(x))}
              toDraft={m.draft} placeholder="–" onSave={(x) => ecrireAn(r, l, champ, m.brute(x), ca)} />
      </td>
    );
  };
  /* Bleue dans la grandeur où elle a été tapée (ou vide, donc à taper). */
  const tenue = (r, forme) => forme == null || forme === (enCA(r) || r.mode === 'amount' ? 'chf' : 'qty');
  const nomRang = (r) => (
    <>
      <OfferIcon name={iconOf(r)} size={15} />{r.name}
      {canWrite && <button type="button" className="lk-edit" title={p("Modifier l'offre")} onClick={(e) => { e.stopPropagation(); ouvrirOffre(r); }}>✎</button>}
      <span className="om-px">{enCA(r) ? puceUnite(dev) : etiquetteUnite(r)}</span>
      {canWrite && r.equation !== 'base_rate' && (
        <button type="button" className="om-add" onClick={(e) => { e.stopPropagation(); ajouterLigne(r); }}>＋ {p('Ligne')}</button>
      )}
    </>
  );
  /* L'ÉCART AVEC LA COMPTABILITÉ (demande d'Olivier, 2026-10-10) : ce que la
     comptabilité porte à fin du dernier mois clos et que le CA RÉALISÉ des
     offres ne couvre pas. Du chiffre d'affaires : il se lit dans la carte du
     CA et dans la Synthèse, jamais dans les ventes. Sans Finance, ou sans mois
     clos, il ne se lit pas. */
  const ecartCompta = vue && moisClos && caReel != null
    ? caReel - vue.rows.reduce((a, r) => a + r.lines.reduce((b, l) => b + (annee(l).ca_actual || 0), 0), 0)
    : null;
  /* EN VALEUR, une ligne TOTAL ferme la carte des ventes (demande d'Olivier,
     2026-10-10) : toutes les colonnes y sont en francs de valeur vendue. */
  const EN_FRANCS = { mode: 'amount' };
  const basculerCA = (id) => setCaOuvertes((x) => {
    const n = new Set(x);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  /* MANUEL (demande d'Olivier, 2026-10-10) : les mois affichés se figent, un
     mois tapé ne déplace plus les autres, et l'écart à l'année se reporte d'un
     geste (« Σ → Année ») plutôt que de se répartir. */
  // La montée en charge est un profil depuis le 2026-10-11, modifiable.
  const optionsRegles = [{ value: 'linear', label: p('linéaire') },
                         { value: 'manual', label: p('manuel') },
                         ...profils.map((x) => ({ value: `profile:${x.key}`, label: x.name }))];
  /* L'ÉCRAN DU CHIFFRE D'AFFAIRES, la Synthèse (décision d'Olivier,
     2026-10-11) : le CA RÉALISÉ se TAPE ici (bleu, `actual_ca`), seul réalisé
     des ventes ; puis le CA que le portefeuille et les nouvelles ventes donnent
     sur les mois à venir. L'écart à la cible porte sur les offres, puis
     l'écart avec la comptabilité mène au total comptable. */
  /* Une LIGNE DE DÉTAIL se rattache à son offre par un filet, aligné sur le
     texte de l'offre (demande d'Olivier, 2026-10-11) ; la dernière le coude. */
  const detailCls = (i, n) => ` om-detail${i === n - 1 ? ' om-detail--der' : ''}`;
  const caOuv = (x, prt) => cols.reduce((a, c) => a + ((((x.parts || {})[prt] || {}).ca || [])[c.i] || 0), 0);
  const caDe = (l) => {
    const fc = annee(l);
    const reel = moisClos ? (fc.ca_actual ?? 0) : 0;
    const bk = caOuv(l, 'bk');
    const neuf = caOuv(l, 'new');
    return { cible: l.target ? l.target.value : null, reel, bk, neuf, att: reel + bk + neuf,
             report: (l.carry || {}).ca ?? 0, cmp: l.compare ? l.compare.value : null, tape: fc.ca_actual != null };
  };
  const caOffre = (r) => {
    const vs = r.lines.map(caDe);
    const s = (k) => vs.reduce((a, x) => a + (x[k] || 0), 0);
    return { cible: r.target ? r.target.value : null, reel: s('reel'), bk: s('bk'), neuf: s('neuf'), att: s('att'),
             report: (r.carry || {}).ca ?? s('report'), cmp: r.compare ? r.compare.value : null };
  };
  /* LE CA RÉALISÉ SE LIT ICI, il se saisit dans la section Réalisé (2026-10-20) : mois par
     mois, sur des lignes rattachées à l'offre et, en option, à une ligne de planification.
     Une ligne de planification sans historique rattaché dit « n/a » : on ne le connaît pas. */
  const celluleCA = (r, l, v, parLigne = false) => {
    if (!moisClos) return <td className="lk-num">–</td>;
    if (parLigne && !v.tape) return <td className="lk-num om-der">{NA}</td>;
    return <td className="lk-num om-der">{val(v.reel || null)}</td>;
  };
  /* LA TABLE REGROUPÉE (version A du proto du 2026-10-11) : trois groupes
     nommés, l'atterrissage (réalisé + portefeuille + nouvelles ventes = total,
     le total sur un fond de couleur), puis le report ; derrière un filet, la
     cible et son écart, puis la composition de l'atterrissage en barre ;
     derrière un autre, le plan comparé. La table prend la largeur de ses
     colonnes. */
  const caRangs = vue ? vue.rows.map((r) => ({ r, v: caOffre(r) })) : [];
  const caTot = vue ? (() => {
    const tot = { cible: 0, reel: 0, bk: 0, neuf: 0, att: 0, report: 0, cmp: 0 };
    let cibleConnue = true;
    caRangs.forEach(({ v }) => {
      if (v.cible == null) cibleConnue = false; else tot.cible += v.cible;
      ['reel', 'bk', 'neuf', 'att', 'report'].forEach((k) => { tot[k] += v[k] || 0; });
      tot.cmp += v.cmp || 0;
    });
    return { ...tot, cible: cibleConnue ? tot.cible : null, cmp: avecCmp ? tot.cmp : null };
  })() : null;
  const rangCA = (v, reel = null, gras = false) => (
    <>
      {reel || <td className="lk-num">{moisClos ? val(v.reel || null) : '–'}</td>}
      <td className="lk-num">{v.bk == null ? '' : val(v.bk || null)}</td>
      <td className="lk-num">{v.neuf == null ? '' : val(v.neuf || null)}</td>
      <td className={`lk-num om-att${gras ? ' om-fort' : ''}`}>{val(v.att || null)}</td>
      <td className="lk-num">{v.report == null ? '' : val(v.report || null)}</td>
      <td className="lk-num lk-sep">{v.cible == null ? '' : val(v.cible || null)}</td>
      <td className="lk-num">{v.cible == null ? '' : ecartCHF(v.att - v.cible)}</td>
      {avecCmp && <><td className="lk-num lk-sep">{v.cmp == null ? '' : val(v.cmp)}</td>
        <td className="lk-num">{v.cmp == null ? '' : ecartCHF(v.att - v.cmp)}</td></>}
    </>
  );
  const ecartCHF = (v) => (v == null ? NA
    : <span className={v > 0 ? 'om-pos' : v < 0 ? 'om-neg' : ''}>{Math.abs(v) < (unit === 'kchf' ? 500 : 0.5) ? '–' : `${v > 0 ? '+' : ''}${val(v)}`}</span>);
  const op = (x) => <span className="om-op" aria-hidden="true">{x}</span>;
  const avecReprise = reprise != null && Math.abs(reprise) >= (unit === 'kchf' ? 500 : 0.5);
  const tableCAAn = vue && (() => {
    const lignes = [];
    caRangs.forEach(({ r, v }) => {
      const ouverte = caOuvertes.has(r.offer_id);
      lignes.push(
        <tr key={r.offer_id} className={`om-row om-offre${r.has_lines ? ' om-cliquable' : ' om-compta'}${ouverte ? ' is-selected' : ''}`}
            onClick={r.has_lines ? () => basculerCA(r.offer_id) : undefined}>
          <td className="om-lbl" title={r.name}>
            <OfferIcon name={iconOf(r)} size={15} />{r.name}
            {r.has_lines && <span className={`om-pli${ouverte ? ' is-open' : ''}`} aria-hidden="true">›</span>}
          </td>
          {rangCA(v, r.has_lines ? null : celluleCA(r, r.lines[0], caDe(r.lines[0])), true)}
        </tr>,
      );
      if (r.has_lines && ouverte) {
        r.lines.forEach((l, i) => {
          const vl = caDe(l);
          lignes.push(
            <tr key={`${r.offer_id}-${l.line_id}`} className={`om-row om-li${detailCls(i, r.lines.length)}`}>
              <td className="om-lbl" title={l.label}>{l.label}</td>
              {rangCA(vl, celluleCA(r, l, vl, true))}
            </tr>,
          );
        });
      }
    });
    const tot = caTot;
    const attAligne = tot.att + (ecartCompta || 0) + (avecReprise ? reprise : 0);
    const vides = (
      <>
        <td className="lk-num lk-sep" /><td className="lk-num" />
        {avecCmp && <><td className="lk-num lk-sep" /><td className="lk-num" /></>}
      </>
    );
    return (
      <div className="om-scroll lk-hscroll">
        <table className="lk-entry-table om-table om-an-table">
          <colgroup>
            <col style={{ width: 236 }} />
            {Array.from({ length: 7 }, (_, i) => <col key={i} style={{ width: 120 }} />)}
            {avecCmp && <><col style={{ width: 120 }} /><col style={{ width: 120 }} /></>}
          </colgroup>
          <thead>
            <tr className="om-grp">
              <th className="om-lbl" />
              <th className="om-grp__g" colSpan={4}>{`${p('Atterrissage')} ${an0}`}</th>
              <th />
              <th className="om-grp__g lk-sep" colSpan={2}>{p('Cible')}<br />{nomCible}</th>
              {avecCmp && <th className="om-grp__g lk-sep" colSpan={2}>&nbsp;<br />{nomPlan(vue.compare)}</th>}
            </tr>
            <tr>
              <th className="om-lbl">{p('Offre')}</th>
              <th className="om-th2">{p('CA réalisé')}<br />{finClos ? `${p('à fin')} ${finClos}` : ' '}</th>
              <th className="om-th2">{op('+')}{p('Portefeuille')}<br />&nbsp;</th>
              <th className="om-th2">{op('+')}{p('Nouvelles')}<br />{p('ventes')}</th>
              <th className="om-th2 om-att om-th--fort">{op('=')}{p('Total')}<br />{`${p('CA')} ${an0}`}</th>
              <th className="om-th2">{p('Report')}<br />{`${p('sur')} ${Number(an0) + 1}`}</th>
              <th className="om-th2 lk-sep">{p('Cible')}<br />&nbsp;</th>
              <th className="om-th2">{p('Écart')}<br />{p('à la cible')}</th>
              {avecCmp && <><th className="om-th2 lk-sep">{p('CA')}<br />{an0}</th>
                <th className="om-th2">{p('Écart')}<br />{p('au plan')}</th></>}
            </tr>
          </thead>
          <tbody>
            {lignes}
            <tr className="om-row om-offre om-compta om-an-tot">
              <td className="om-lbl">{p('Sous-total')}</td>
              {rangCA(tot, null, true)}
            </tr>
            {(ecartCompta != null || avecReprise) && (
              <>
                {ecartCompta != null && (
                  <tr className="om-row om-compta om-an-compta">
                    <td className="om-lbl">{p('Écart avec la comptabilité')}</td>
                    <td className="lk-num">{val(ecartCompta)}</td><td className="lk-num" /><td className="lk-num" />
                    <td className="lk-num om-att">{val(ecartCompta)}</td><td className="lk-num" />
                    {vides}
                  </tr>
                )}
                {avecReprise && (
                  <tr className="om-row om-compta om-an-compta">
                    <td className="om-lbl">{p("Reprise d'acomptes clients")}</td>
                    <td className="lk-num" /><td className="lk-num" /><td className="lk-num" />
                    <td className="lk-num om-att">{val(reprise)}</td><td className="lk-num" />
                    {vides}
                  </tr>
                )}
                <tr className="om-row om-tot om-an-aligne">
                  <td className="om-lbl">{p('Total, aligné sur la comptabilité')}</td>
                  <td className="lk-num">{val(tot.reel + (ecartCompta || 0))}</td>
                  <td className="lk-num">{val(tot.bk || null)}</td><td className="lk-num">{val(tot.neuf || null)}</td>
                  <td className="lk-num om-att">{val(attAligne)}</td>
                  <td className="lk-num">{val(tot.report || null)}</td>
                  <td className="lk-num lk-sep">{tot.cible == null ? '' : val(tot.cible || null)}</td>
                  <td className="lk-num">{tot.cible == null ? '' : ecartCHF(attAligne - tot.cible)}</td>
                  {avecCmp && <><td className="lk-num lk-sep">{val(tot.cmp)}</td>
                    <td className="lk-num">{ecartCHF(attAligne - tot.cmp)}</td></>}
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  })();

  /* ── Les VENTES et leurs MOIS, une seule table (variante A, décision
     d'Olivier, 2026-10-11, proto `WIP/protos/repartition-variantes.html`) ──
     L'année à venir, puis ses mois, sur la même ligne ; la règle la ferme.
     PAR OFFRE, une ligne porte le total, et un clic montre ses deux FLUX,
     portefeuille et nouvelles ventes, chacun avec son année, ses mois et sa
     règle. PAR FLUX, deux blocs, le portefeuille puis les nouvelles ventes,
     chacun avec ses offres et leurs lignes. Un mois tapé (bleu) garde sa
     valeur, les autres (noir) se partagent le reste ; les offres ne se
     surlignent que dans la vue Par offre. */
  /* Une ligne montre la règle QU'ELLE SUIT, sans option « selon l'offre »
     (demande d'Olivier, 2026-10-10) : la choisir la lui donne en propre. */
  /* LA COULEUR DIT LA NATURE DE LA RÈGLE (demande d'Olivier, 2026-10-12) : le
     manuel, qu'on tape, reste bleu ; les règles qui répartissent (linéaire,
     profil) se lisent en brun. */
  const natureRegle = (v) => (v === 'manual' ? 'om-prof--manuel' : 'om-prof--regle');
  const choixRegle = (r, l, flux) => (
    <span className={`om-prof ${natureRegle((l.effective_rules || {})[flux] || 'linear')}`} onClick={(e) => e.stopPropagation()}>
      <Choix value={(l.effective_rules || {})[flux] || 'linear'} canWrite={canWrite}
             options={optionsRegles} onSave={(v) => poserRegle(r, l, flux, v || 'linear')} />
    </span>
  );
  const FLUX = [{ id: 'bk', label: p('Portefeuille') }, { id: 'new', label: p('Nouvelles ventes') }];
  const FX = ['bk', 'new'];
  /* Le total en valeur suit ce qui s'affiche : le portefeuille seul, les nouvelles ventes seules, ou les deux. */
  const fluxVus = affichage === 'both' ? FX : [affichage];
  /* EN VOLUMES OU EN VALEUR, comme l'année (demande d'Olivier, 2026-10-10) :
     un mois tapé en francs se garde tel quel, ses unités s'en déduisent au prix. */
  const serieDe = (r, l, flux) => {
    const ca = enCA(r);
    return (flux === 'bk' ? (ca ? l.bk_months : l.backlog) : (ca ? l.new_months : l.new_quantities)) || [];
  };
  const anDe = (r, l, flux) => {
    const fc = annee(l);
    const ca = enCA(r);
    return flux === 'bk' ? ((ca ? fc.backlog_value : fc.backlog) ?? 0) : ((ca ? fc.new_value : fc.new) ?? 0);
  };
  const tapesDe = (l, flux) => (flux === 'bk' ? l.backlog_adjusted : l.adjusted) || [];
  /* Les sommes : l'année et les mois de lignes, sur un flux ou les deux ; les
     deux, c'est le total à venir que portent les lignes. */
  const anSomme = (r, ls, fx) => (fx.length > 1 ? ls.reduce((a, l) => a + (lu(r, l).total || 0), 0)
    : ls.reduce((a, l) => a + anDe(r, l, fx[0]), 0));
  /* Un mois clos se lit sur la PHOTO du plan à la clôture (pour mémoire) ; un mois à venir, sur le plan. */
  const memoDe = (r, l, flux) => {
    const ca = enCA(r);
    return (flux === 'bk' ? (ca ? l.memo_bk_months : l.memo_backlog) : (ca ? l.memo_new_months : l.memo_new)) || [];
  };
  const valeurMois = (r, l, f, c) => (c.clos ? (memoDe(r, l, f)[c.i] || 0) : (serieDe(r, l, f)[c.i] || 0));
  const moisSomme = (r, ls, fx) => colsV.map((c) => ls.reduce((a, l) => a + fx.reduce((b, f) => b + valeurMois(r, l, f, c), 0), 0));
  const tdLu = (r, v, cls = '', key = undefined, g = NOGEL) => {
    const m = mesureAn(r);
    return <td key={key} className={`lk-num${cls}${g.c}`} style={g.s}>{m.fmt(m.aff(v)) || '–'}</td>;
  };
  const moisLus = (r, vals) => vals.map((v, k) => tdLu(r, v, `${colsV[k].sep ? ' lk-sep' : ''}${colsV[k].clos ? ' om-clos' : ' om-prev'}`, colsV[k].i));
  const moisTapes = (r, l, flux) => {
    const m = mesureAn(r);
    const serie = serieDe(r, l, flux);
    const tapes = tapesDe(l, flux);
    return colsV.map((c) => (c.clos ? (
      <td key={c.i} className="lk-num om-clos">{m.fmt(m.aff(memoDe(r, l, flux)[c.i])) || '–'}</td>
    ) : (
      <td key={c.i} className={`lk-num om-prev${c.sep ? ' lk-sep' : ''}${tapes[c.i] ? '' : ' om-der'}`}>
        <Cell value={m.aff(serie[c.i])} type="number" canWrite={canWrite} format={(x) => (x == null ? '' : m.fmt(x) || '–')}
              toDraft={m.draft} placeholder="–" onSave={(x) => ecrireMois(r, l, flux, c.i, m.brute(x), enCA(r))} />
      </td>
    )));
  };
  /* L'année d'une LIGNE, le total planifié, se LIT : c'est la somme du
     portefeuille et des nouvelles ventes, qui se tapent sur leurs flux
     (2026-10-11). */
  /* L'ANNÉE ET SES MOIS SE CORRESPONDENT TOUJOURS (demande d'Olivier,
     2026-10-11) : taper l'année répartit de nouveau ses mois par la règle ; en
     manuel, elle est la somme des mois et se lit. */
  const manuel = (l, flux) => (l.effective_rules || {})[flux] === 'manual';
  const anLigne = (r, l) => {
    return celluleAn(r, l, 'total', lu(r, l).total, { cls: ' lk-sep om-fort', ro: true, g: G('an') });
  };
  const anFlux = (r, l, flux) => {
    const v = lu(r, l);
    const fc = annee(l);
    return flux === 'bk'
      ? celluleAn(r, l, 'backlog', fc.backlog_in ? v.bk : null, { bleu: tenue(r, fc.backlog_in), cls: ' lk-sep', ro: manuel(l, 'bk'), g: G('an') })
      : celluleAn(r, l, 'new', v.neuf, { bleu: tenue(r, fc.new_in), cls: ' lk-sep', ro: manuel(l, 'new'), g: G('an') });
  };
  /* LA RÈGLE SE CHOISIT SUR LES FLUX (demande d'Olivier, 2026-10-12) : on ne
     planifie pas au niveau de la ligne, qui ne porte donc plus de règle ; elle
     vit sur le portefeuille et sur les nouvelles ventes, que « Tout déplier »
     ouvre d'un coup. */
  const regleLigne = () => <td className={`lk-sep om-regle${G('regle').c}`} style={G('regle').s} />;
  /* La règle d'un FLUX, seule dans sa cellule (2026-10-11) : l'année et ses
     mois se correspondant toujours, plus rien à reporter ni à répartir. */
  const regleFlux = (r, l, flux) => (
    <td className={`lk-sep om-regle${G('regle').c}`} style={G('regle').s}>{choixRegle(r, l, flux)}</td>
  );
  /* LA RECHERCHE (demande d'Olivier, 2026-10-10) : une offre retenue montre
     toutes ses lignes ; une ligne retenue montre son offre, réduite à elle. */
  const offreRetenue = (r) => filtreRech.has(`o:${r.offer_id}`);
  /* La ligne À PART d'une offre (son CA réalisé sans ligne de planification) se lit dans le chiffre d'affaires, jamais dans les ventes. */
  const reelles = (r) => r.lines.filter((l) => !l.realise_seul);
  const lignesVues = (r) => (!filtreRech.size || offreRetenue(r) ? reelles(r)
    : reelles(r).filter((l) => l.line_id && filtreRech.has(`l:${l.line_id}`)));
  const offresVues = vue ? vue.rows.filter((r) => lignesVues(r).length > 0) : [];
  /* Les offres où l'on peut ajouter une ligne : toutes, sauf une commission, dont le taux vaut pour toute l'assiette. */
  const offresAjout = vue ? vue.rows.filter((r) => r.equation !== 'base_rate') : [];
  const pli = (ouverte) => <span className={`om-pli${ouverte ? ' is-open' : ''}`} aria-hidden="true">›</span>;
  const cleL = (r, l) => l.line_id || `o:${r.offer_id}`;
  const basculerFlux = (k) => setFluxOuverts((x) => {
    const n = new Set(x);
    if (n.has(k)) n.delete(k); else n.add(k);
    return n;
  });
  /* TOUT DÉPLIER, TOUT REPLIER (demande d'Olivier, 2026-10-12) : par offre, une
     ligne s'ouvre sur ses deux flux d'un clic ; ce bouton ouvre ou referme toutes
     celles qu'on voit, pour atteindre d'un coup les lignes de saisie. */
  const clesLignes = offresVues.flatMap((r) => lignesVues(r).map((l) => cleL(r, l)));
  const toutOuvert = clesLignes.length > 0 && clesLignes.every((k) => fluxOuverts.has(k));
  const basculerTout = () => setFluxOuverts(toutOuvert ? new Set() : new Set(clesLignes));
  const itemsRech = vue ? [
    ...vue.rows.map((r) => ({ id: `o:${r.offer_id}`, label: r.name, kind: p('Offre') })),
    ...vue.rows.flatMap((r) => reelles(r).filter((l) => l.line_id).map((l) => ({ id: `l:${l.line_id}`, label: `${l.label} · ${r.name}`, kind: p('Ligne') }))),
  ] : [];
  /* PAR OFFRE : l'offre surlignée, ses lignes, et sous une ligne ouverte ses deux flux. */
  const rangsFlux = (r, l, sous) => FLUX.map((f) => (
    <tr key={f.id} className={`om-row om-fl${sous ? ' om-fl--li' : ''}`}>
      <td className={`om-lbl${G('lbl').c}`} style={G('lbl').s}>{f.label}</td>
      <td className={`lk-num${G('prix').c}`} style={G('prix').s} />
<td className={`lk-num${G('cout').c}`} style={G('cout').s} />
      {regleFlux(r, l, f.id)}
      {anFlux(r, l, f.id)}
      {moisTapes(r, l, f.id)}
    </tr>
  ));
  const parOffre = () => offresVues.map((r) => {
    const lv = lignesVues(r);
    if (!r.has_lines) {
      const l = lv[0];
      const ouverte = fluxOuverts.has(cleL(r, l));
      return (
        <React.Fragment key={r.offer_id}>
          <tr className="om-row om-offre om-hi om-cliquable" onClick={() => basculerFlux(cleL(r, l))}>
            <td className={`om-lbl${G('lbl').c}`} style={G('lbl').s} title={r.name}>{nomRang(r)}{pli(ouverte)}</td>
            {cellulePrix(r, l, G('prix'))}
{celluleCout(r, l, G('cout'))}
            {regleLigne()}
            {anLigne(r, l)}
            {moisLus(r, moisSomme(r, [l], FX))}
          </tr>
          {ouverte && rangsFlux(r, l, false)}
        </React.Fragment>
      );
    }
    return (
      <React.Fragment key={r.offer_id}>
        <tr className="om-row om-offre om-hi">
          <td className={`om-lbl${G('lbl').c}`} style={G('lbl').s} title={r.name}>{nomRang(r)}</td>
          <td className={`lk-num${G('prix').c}`} style={G('prix').s} />
<td className={`lk-num${G('cout').c}`} style={G('cout').s} />
          <td className={`lk-sep${G('regle').c}`} style={G('regle').s} />
          {tdLu(r, anSomme(r, lv, FX), ' lk-sep om-fort', undefined, G('an'))}
          {moisLus(r, moisSomme(r, lv, FX))}
        </tr>
        {lv.map((l, i) => {
          const ouverte = fluxOuverts.has(cleL(r, l));
          return (
            <React.Fragment key={l.line_id}>
              <tr className={`om-row om-li om-cliquable${detailCls(i, lv.length)}`} onClick={() => basculerFlux(cleL(r, l))}>
                <td className={`om-lbl${G('lbl').c}`} style={G('lbl').s} title={l.label}>
                  {canWrite ? <Cell value={l.label} canWrite onSave={(x) => x && renommer(l, x)} /> : l.label}
                  {pli(ouverte)}
                  {canWrite && <button type="button" className="lk-x lk-del om-del" aria-label={p('Supprimer')}
                                       onClick={(e) => { e.stopPropagation(); supprimer(l); }}>✕</button>}
                </td>
                {cellulePrix(r, l, G('prix'))}
{celluleCout(r, l, G('cout'))}
                {regleLigne()}
                {anLigne(r, l)}
                {moisLus(r, moisSomme(r, [l], FX))}
              </tr>
              {ouverte && rangsFlux(r, l, true)}
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  });
  /* PAR FLUX : deux blocs ; leur total ne se lit qu'en valeur, des pièces et
     des heures ne s'additionnant pas. */
  const enValeur = modeReel === 'chf';
  const vides = (cls = '') => colsV.map((c) => <td key={c.i} className={`lk-num${c.sep ? ' lk-sep' : ''}${c.clos ? '' : ' om-prev'}${cls}`} />);
  const totalFlux = (fx) => offresVues.reduce((a, r) => a + anSomme(r, lignesVues(r), fx), 0);
  const moisFlux = (fx) => colsV.map((c, k) => offresVues.reduce((a, r) => a + moisSomme(r, lignesVues(r), fx)[k], 0));
  const parFlux = () => FLUX.filter((f) => affichage === 'both' || f.id === affichage).map((f, b) => (
    <React.Fragment key={f.id}>
      {b > 0 && <tr className="om-vide"><td colSpan={5 + colsV.length} /></tr>}
      <tr className="om-row om-bloc">
        <td className={`om-lbl${G('lbl').c}`} style={G('lbl').s}>{f.label}</td>
        <td className={`lk-num${G('prix').c}`} style={G('prix').s} />
<td className={`lk-num${G('cout').c}`} style={G('cout').s} />
        <td className={`lk-sep${G('regle').c}`} style={G('regle').s} />
        {enValeur ? tdLu(EN_FRANCS, totalFlux([f.id]), ' lk-sep om-fort', undefined, G('an'))
          : <td className={`lk-sep${G('an').c}`} style={G('an').s} />}
        {enValeur ? moisLus(EN_FRANCS, moisFlux([f.id])) : vides()}
      </tr>
      {offresVues.map((r) => {
        const lv = lignesVues(r);
        const nom = (
          <>
            {r.name}
            {canWrite && <button type="button" className="lk-edit" title={p("Modifier l'offre")} onClick={(e) => { e.stopPropagation(); ouvrirOffre(r); }}>✎</button>}
            <span className="om-px">{enCA(r) ? puceUnite(dev) : etiquetteUnite(r)}</span>
          </>
        );
        if (!r.has_lines) {
          return (
            <tr key={r.offer_id} className="om-row om-offre">
              <td className={`om-lbl${G('lbl').c}`} style={G('lbl').s} title={r.name}>{nom}</td>
              {cellulePrix(r, lv[0], G('prix'))}
{celluleCout(r, lv[0], G('cout'))}
              {regleFlux(r, lv[0], f.id)}
              {anFlux(r, lv[0], f.id)}
              {moisTapes(r, lv[0], f.id)}
            </tr>
          );
        }
        return (
          <React.Fragment key={r.offer_id}>
            <tr className="om-row om-offre">
              <td className={`om-lbl${G('lbl').c}`} style={G('lbl').s} title={r.name}>{nom}</td>
              <td className={`lk-num${G('prix').c}`} style={G('prix').s} />
<td className={`lk-num${G('cout').c}`} style={G('cout').s} />
              <td className={`lk-sep${G('regle').c}`} style={G('regle').s} />
              {tdLu(r, anSomme(r, lv, [f.id]), ' lk-sep om-fort', undefined, G('an'))}
              {moisLus(r, moisSomme(r, lv, [f.id]))}
            </tr>
            {lv.map((l, i) => (
              <tr key={l.line_id} className={`om-row om-li${detailCls(i, lv.length)}`}>
                <td className={`om-lbl${G('lbl').c}`} style={G('lbl').s} title={l.label}>{l.label}</td>
                {cellulePrix(r, l, G('prix'))}
{celluleCout(r, l, G('cout'))}
                {regleFlux(r, l, f.id)}
                {anFlux(r, l, f.id)}
                {moisTapes(r, l, f.id)}
              </tr>
            ))}
          </React.Fragment>
        );
      })}
    </React.Fragment>
  ));
  const tableVentes = vue && (
    <div className="om-scroll lk-hscroll">
      <table className={`lk-entry-table om-table om-an-table om-ventes${axe === 'flux' ? ' om-ventes--flux' : ''}`}>
        <colgroup><col style={{ width: LARG.lbl }} /><col style={{ width: LARG.prix }} /><col style={{ width: LARG.cout }} />
          <col style={{ width: LARG.regle }} /><col style={{ width: LARG.an }} />
          {colsV.map((c) => <col key={c.i} style={{ width: unit === 'kchf' ? 64 : 88 }} />)}</colgroup>
        <thead>
          {/* LE BANDEAU RÉALISÉ / PRÉVISIONNEL (standard de la charte) au-dessus des mois. */}
          <tr className="om-bandes">
            <th colSpan={5} />
            {nbClos > 0 && <th className="lk-band lk-band--real" colSpan={nbClos}>{p('Plan à la clôture')}</th>}
            {ouverts.length > 0 && <th className="lk-band lk-band--fore" colSpan={ouverts.length}>{p('Planification')}</th>}
          </tr>
          <tr>
            <th className={`om-lbl${G('lbl').c}`} style={G('lbl').s}>{p('Offre')}</th>
            <th colSpan={2} className={`om-th2${G('prix').c}`} style={G('prix').s}>{p('Prix · Coût')}<br />CHF · %</th>
            <th className={`lk-sep om-regle${G('regle').c}`} style={G('regle').s}>{p('Profil')}</th>
            <th className={`om-th2 lk-sep${G('an').c}`} style={G('an').s}>{p('Total planifié')}<br />{an0}</th>
            {colsV.map((c) => <th key={c.i} className={`${c.sep ? 'lk-sep' : ''}${c.clos ? ' om-pass' : ' om-prev'}`}>{c.label}</th>)}
          </tr>
        </thead>
        <tbody>
          {axe === 'flux' ? parFlux() : parOffre()}
          {enValeur && (
            <tr className="om-row om-tot">
              <td className={`om-lbl${G('lbl').c}`} style={G('lbl').s}>{p('Total')}<span className="om-px">{dev}</span></td>
              <td className={`lk-num${G('prix').c}`} style={G('prix').s} />
<td className={`lk-num${G('cout').c}`} style={G('cout').s} />
              <td className={`lk-sep${G('regle').c}`} style={G('regle').s} />
              {tdLu(EN_FRANCS, totalFlux(fluxVus), ' lk-sep', undefined, G('an'))}
              {moisLus(EN_FRANCS, moisFlux(fluxVus))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
  /* Les PROFILS de saisonnalité : créés une fois, choisis par autant d'offres
     qu'on veut ; « Utilisé par » dit ce qu'une modification touche. */
  const profilAppel = async (promesse) => {
    const r = await promesse;
    if (r && r.error) { addToast && addToast(r.error, 'error'); return; }
    await reload();
  };

  /* ── Les impacts financiers, en francs ──────────────────────────────── */
  const RECONNU = (tr) => (tr.recognition === 'spread' && tr.period_months > 1 ? `${p('sur')} ${tr.period_months} ${p('mois')}` : p('à la livraison'));

  /* Le CHIFFRE D'AFFAIRES PAR MOIS (2026-10-11) : le CA reconnu des mois à
     venir, plan et ventes réalisées confondus, puis ce qui part sur l'année
     suivante. Un clic sur une offre montre ses lignes. */
  const tableCAMois = vue && (() => {
    const caDeX = (x) => x.ca || [];
    const rang = (x, cle, cls, libelle, onClick) => (
      <tr key={cle} className={`om-row ${cls}`} onClick={onClick}>
        <td className="om-lbl">{libelle}</td>
        <td className="lk-num om-an">{val(cols.reduce((a, c) => a + (caDeX(x)[c.i] || 0), 0) || null)}</td>
        {cols.map((c) => <td key={c.i} className={`lk-num${c.sep ? ' lk-sep' : ''}`}>{val(caDeX(x)[c.i] || null)}</td>)}
        <td className="lk-num lk-sep">{val(((x.carry || {}).ca) || null)}</td>
      </tr>
    );
    const lignes = [rang({ ...vue.total, carry: vue.total_carry }, 'total', 'om-tot', p('CA reconnu'))];
    vue.rows.forEach((r) => {
      const ouverte = caOuvertes.has(r.offer_id);
      lignes.push(rang(r, r.offer_id, `om-sous-li${r.has_lines ? ' om-cliquable' : ''}`,
                       <>{r.name}<span className="om-px">{RECONNU(r.terms || {})}</span>{r.has_lines && pli(ouverte)}</>,
                       r.has_lines ? () => basculerCA(r.offer_id) : undefined));
      if (r.has_lines && ouverte) r.lines.forEach((l, i) => lignes.push(rang(l, `${r.offer_id}-${l.line_id}`, `om-sous-li${detailCls(i, r.lines.length)}`, l.label)));
    });
    return (
      <div className="om-scroll lk-hscroll">
        <table className="lk-entry-table om-table">
          <colgroup><col style={{ width: 300 }} /><col style={{ width: 104 }} />
            {cols.map((c) => <col key={c.i} style={{ width: unit === 'kchf' ? 64 : 96 }} />)}<col style={{ width: 104 }} /></colgroup>
          <thead>
            <tr>
              <th className="om-lbl">{`${p('Entreprise')} · ${dev}`}</th>
              <th className="om-th2 om-an">{p('Total')}<br />{cols.length ? `${cols[0].label} → ${cols[cols.length - 1].label}` : ' '}</th>
              {cols.map((c) => <th key={c.i} className={c.sep ? 'lk-sep' : ''}>{c.label}</th>)}
              <th className="om-th2 lk-sep">{p('Report')}<br />{Number(an0) + 1}</th>
            </tr>
          </thead>
          <tbody>{lignes}</tbody>
        </table>
      </div>
    );
  })();

  /* ── La SYNTHÈSE : le chiffre d'affaires et la facturation par offre ── */
  const surAn = (serie) => (serie || []).slice(0, 12).reduce((a, b) => a + (b || 0), 0);
  const partAn = (x, prt, k) => surAn((((x.parts || {})[prt]) || {})[k]);
  const ligneOuverte = (carte, id) => ouvertes[carte].has(id);
  const basculerOuverte = (carte, id) => setOuvertes((x) => {
    const n = { ...x, [carte]: new Set(x[carte]) };
    if (n[carte].has(id)) n[carte].delete(id); else n[carte].add(id);
    return n;
  });
  const nomOffre = (r, carte) => (
    <>
      <OfferIcon name={iconOf(r)} size={15} />{r.name}
      {r.has_lines && <span className={`om-pli${ligneOuverte(carte, r.offer_id) ? ' is-open' : ''}`} aria-hidden="true">›</span>}
    </>
  );
  /* Le CHIFFRE D'AFFAIRES PAR OFFRE de la Synthèse (remis le 2026-10-11, à la
     demande d'Olivier, sans les effets volume et prix : il n'y a plus de
     volume réalisé) : en lecture, le CA réalisé se tapant dans les Prévisions.
     L'écart avec la comptabilité mène au total comptable. */
  const tableSyntheseCA = vue && (() => {
    const lignes = [];
    const tot = { cible: 0, reel: 0, bk: 0, neuf: 0, att: 0, cmp: 0 };
    let cibleConnue = true;
    const rang = (v, cle, cls, libelle, onClick) => (
      <tr key={cle} className={`om-row ${cls}`} onClick={onClick}>
        <td className="om-lbl">{libelle}</td>
        <td className="lk-num">{v.cible == null ? NA : val(v.cible || null)}</td>
        <td className="lk-num lk-sep">{moisClos ? val(v.reel || null) : '–'}</td>
        <td className="lk-num">{val(v.bk || null)}</td>
        <td className="lk-num">{val(v.neuf || null)}</td>
        <td className="lk-num om-fort">{val(v.att || null)}</td>
        <td className="lk-num lk-sep">{v.cible == null ? NA : ecartCHF(v.att - v.cible)}</td>
        {avecCmp && <><td className="lk-num lk-sep">{v.cmp == null ? NA : val(v.cmp)}</td>
          <td className="lk-num">{v.cmp == null ? NA : ecartCHF(v.att - v.cmp)}</td></>}
      </tr>
    );
    vue.rows.forEach((r) => {
      const v = caOffre(r);
      const ouverte = ligneOuverte('ca', r.offer_id);
      if (v.cible == null) cibleConnue = false; else tot.cible += v.cible;
      ['reel', 'bk', 'neuf', 'att'].forEach((k) => { tot[k] += v[k] || 0; });
      tot.cmp += v.cmp || 0;
      lignes.push(rang(v, r.offer_id, `om-offre${r.has_lines ? ' om-cliquable' : ''}`, nomOffre(r, 'ca'),
                       r.has_lines ? () => basculerOuverte('ca', r.offer_id) : undefined));
      if (r.has_lines && ouverte) {
        r.lines.forEach((l, i) => lignes.push(rang(caDe(l), `${r.offer_id}-${l.line_id}`, `om-li${detailCls(i, r.lines.length)}`, l.label)));
      }
    });
    if (ecartCompta != null) {
      lignes.push(
        <tr key="compta" className="om-row om-offre om-compta">
          <td className="om-lbl">{p('Écart avec la comptabilité')}</td>
          <td className="lk-num" /><td className="lk-num lk-sep">{val(ecartCompta)}</td>
          <td className="lk-num" /><td className="lk-num" />
          <td className="lk-num om-fort">{val(ecartCompta)}</td><td className="lk-num lk-sep" />
          {avecCmp && <><td className="lk-num lk-sep" /><td className="lk-num" /></>}
        </tr>,
      );
    }
    const reel = tot.reel + (ecartCompta || 0);
    const att = tot.att + (ecartCompta || 0);
    return (
      <div className="om-scroll lk-hscroll">
        <table className="lk-entry-table om-table om-reel">
          <colgroup><col style={{ width: 300 }} />{Array.from({ length: 6 + (avecCmp ? 2 : 0) }, (_, i) => <col key={i} style={{ width: 108 }} />)}</colgroup>
          <thead>
            <tr>
              <th className="om-lbl">{p('Offre')}</th>
              <th className="om-th2">{p('Cible')}<br />{nomCible}</th>
              <th className="om-th2 lk-sep">{p('Réalisé')}<br />{finClos ? `${p('à fin')} ${finClos}` : ' '}</th>
              <th className="om-th2">{p('Portefeuille')}<br />&nbsp;</th>
              <th className="om-th2">{p('Nouvelles')}<br />{p('ventes')}</th>
              <th className="om-th2 om-th--fort">{p('Total')}<br />{p('prévisionnel')}</th>
              <th className="om-th2 lk-sep">{p('Écart')}<br />{p('à la cible')}</th>
              {avecCmp && <><th className="om-th2 lk-sep">{nomPlan(vue.compare)}<br />{p('comparaison')}</th>
                <th className="om-th2">{p('Écart')}<br />{`${p('à')} ${nomPlan(vue.compare)}`}</th></>}
            </tr>
          </thead>
          <tbody>
            {lignes}
            <tr className="om-tot">
              <td className="om-lbl">{p('Total')}</td>
              <td className="lk-num">{cibleConnue ? val(tot.cible || null) : NA}</td>
              <td className="lk-num lk-sep">{val(reel || null)}</td>
              <td className="lk-num">{val(tot.bk || null)}</td><td className="lk-num">{val(tot.neuf || null)}</td>
              <td className="lk-num">{val(att)}</td>
              <td className="lk-num lk-sep">{cibleConnue ? ecartCHF(att - tot.cible) : NA}</td>
              {avecCmp && <><td className="lk-num lk-sep">{val(tot.cmp)}</td><td className="lk-num">{ecartCHF(att - tot.cmp)}</td></>}
            </tr>
          </tbody>
        </table>
      </div>
    );
  })();
  const tableFacturation = vue && (() => {
    const lignes = [];
    const tot = { obj: 0, objConnu: true, fac: 0, bk: 0, neuf: 0, connu: true, cmp: 0 };
    vue.rows.forEach((r) => {
      const fac = (r.billed || {})[an0] ?? null;
      const obj = r.target ? r.target.value : null;
      const bk = partAn(r, 'bk', 'billing');
      const neuf = partAn(r, 'new', 'billing');
      if (obj == null) tot.objConnu = false; else tot.obj += obj;
      tot.bk += bk; tot.neuf += neuf; tot.cmp += r.compare ? r.compare.value : 0;
      if (fac == null) tot.connu = false; else tot.fac += fac;
      const cliquable = r.has_lines;
      const total = fac == null ? null : fac + bk + neuf;
      lignes.push(
        <tr key={r.offer_id} className={`om-row om-offre${cliquable ? ' om-cliquable' : ''}`}
            onClick={cliquable ? () => basculerOuverte('fac', r.offer_id) : undefined}>
          <td className="om-lbl">{nomOffre(r, 'fac')}</td>
          <td className="lk-num">{obj == null ? NA : val(obj || null)}</td>
          <td className="lk-num lk-sep">{fac == null ? NA : val(fac || null)}</td>
          <td className="lk-num">{val(bk || null)}</td><td className="lk-num">{val(neuf || null)}</td>
          <td className="lk-num om-fort">{total == null ? NA : val(total)}</td>
          {avecCmp && <><td className="lk-num lk-sep">{r.compare ? val(r.compare.value) : NA}</td>
            <td className="lk-num">{r.compare && total != null ? ecartCHF(total - r.compare.value) : NA}</td></>}
        </tr>,
      );
      if (cliquable && ligneOuverte('fac', r.offer_id)) {
        r.lines.forEach((l) => {
          lignes.push(
            <tr key={`${r.offer_id}-${l.line_id}`} className="om-row om-li">
              <td className="om-lbl">{l.label}</td>
              <td className="lk-num">{l.target ? val(l.target.value || null) : NA}</td>
              <td className="lk-num lk-sep">{NA}</td>
              <td className="lk-num">{val(partAn(l, 'bk', 'billing') || null)}</td>
              <td className="lk-num">{val(partAn(l, 'new', 'billing') || null)}</td>
              <td className="lk-num">{NA}</td>
              {avecCmp && <><td className="lk-num lk-sep">{l.compare ? val(l.compare.value) : NA}</td><td className="lk-num">{NA}</td></>}
            </tr>,
          );
        });
      }
    });
    const aPart = (vue.billed_unassigned || {})[an0];
    if (aPart != null) tot.fac += aPart;
    const totalF = tot.connu ? tot.fac + tot.bk + tot.neuf : null;
    return (
      <div className="om-scroll lk-hscroll">
        <table className="lk-entry-table om-table om-reel">
          <colgroup><col style={{ width: 300 }} />{Array.from({ length: 5 + (avecCmp ? 2 : 0) }, (_, i) => <col key={i} style={{ width: 120 }} />)}</colgroup>
          <thead>
            <tr>
              <th className="om-lbl">{p('Offre')}</th>
              <th className="om-th2">{p('Cible')}<br />{nomCible}</th>
              <th className="om-th2 lk-sep">{p('Facturé')}<br />{finClos ? `${p('à fin')} ${finClos}` : ' '}</th>
              <th className="om-th2">{p('Portefeuille')}<br />{p('à facturer')}</th>
              <th className="om-th2">{p('Nouvelles ventes')}<br />{p('à facturer')}</th>
              <th className="om-th2 om-th--fort">{p('Total')}<br />{p('facturation')}</th>
              {avecCmp && <><th className="om-th2 lk-sep">{nomPlan(vue.compare)}<br />{p('comparaison')}</th>
                <th className="om-th2">{p('Écart')}<br />{`${p('à')} ${nomPlan(vue.compare)}`}</th></>}
            </tr>
          </thead>
          <tbody>
            {lignes}
            {aPart != null && (
              <tr className="om-row om-offre">
                <td className="om-lbl om-calc">{p('Non rattaché à une offre')}</td>
                <td className="lk-num" /><td className="lk-num lk-sep">{val(aPart || null)}</td>
                <td className="lk-num">–</td><td className="lk-num">–</td><td className="lk-num om-fort">{val(aPart || null)}</td>
                {avecCmp && <><td className="lk-num lk-sep" /><td className="lk-num" /></>}
              </tr>
            )}
            <tr className="om-tot">
              <td className="om-lbl">{p('Total')}</td>
              <td className="lk-num">{tot.objConnu ? val(tot.obj || null) : NA}</td>
              <td className="lk-num lk-sep">{tot.connu ? val(tot.fac || null) : NA}</td>
              <td className="lk-num">{val(tot.bk || null)}</td><td className="lk-num">{val(tot.neuf || null)}</td>
              <td className="lk-num">{totalF == null ? NA : val(totalF)}</td>
              {avecCmp && <><td className="lk-num lk-sep">{val(tot.cmp)}</td><td className="lk-num">{totalF == null ? NA : ecartCHF(totalF - tot.cmp)}</td></>}
            </tr>
          </tbody>
        </table>
      </div>
    );
  })();

  if (!tid) {
    return <PageShell framed title={titre}><p className="om-empty">{p('Choisir un client.')}</p></PageShell>;
  }
  const vide = vue && vue.rows.length === 0;
  if (!prevision) {
    return (
      <PageShell framed title={titre} exportRef={exportRef} unit={pageUnit} views={vuesPage}>
        {/* La SYNTHÈSE se lit (décision d'Olivier, 2026-10-11) : l'atterrissage,
            le chiffre d'affaires par offre, la facturation ; le CA réalisé se
            tape dans les Prévisions. */}
        <OfferYearCard view={view} year={year} unit={unit} p={p} caReel={caReel} />
        <div className="om-bloc" />
        <CardShell title={`${p("Chiffre d'affaires")} ${an0} ${p('par offre')} · ${dev}`} flush>
          {vide ? <div className="om-empty">{p('Aucune offre active.')}</div> : tableSyntheseCA}
        </CardShell>
        <div className="om-bloc" />
        <CardShell title={`${p('Facturation')} ${an0} ${p('par offre')} · ${dev}`} flush>
          {vide ? <div className="om-empty">{p('Aucune offre active.')}</div> : tableFacturation}
        </CardShell>
      </PageShell>
    );
  }
  const sansOffre = <div className="om-empty">{p('Aucune offre active.')}</div>;
  /* L'ATTERRISSAGE EN CA, à droite des ventes (proto
     `WIP/protos/ventes-atterrissage.html`, validé le 2026-10-11) : le CA
     réalisé, plus ce que le portefeuille et les nouvelles ventes donnent
     d'ici décembre, face à la cible ; puis chaque offre. Il suit ce qu'on tape,
     sans rien conseiller ni filtrer. */
  const panneauCA = vue && !vide ? (() => {
    const vs = vue.rows.map((r) => ({ r, v: caOffre(r) }));
    const t = (k) => vs.reduce((a, x) => a + (x.v[k] || 0), 0);
    const cible = vs.some((x) => x.v.cible == null) ? null : t('cible');
    const att = t('att');
    const echelle = Math.max(att, cible || 0) || 1;
    const part = (v) => `${Math.max(0, (v / echelle) * 100)}%`;
    const carte = (
      <CardShell title={`${p('Atterrissage')} · ${p('CA')} ${an0}`}
                 className={`om-panneau${atterrissageLibre ? ` om-panneau--flottant${atterrissageVu ? '' : ' is-cache'}` : ''}`}
                 primaryAction={(
                   <button type="button" className={`om-punaise${atterrissageLibre ? '' : ' is-on'}`}
                           title={atterrissageLibre ? p("Épingler l'atterrissage") : p("Libérer l'atterrissage")}
                           aria-label={atterrissageLibre ? p("Épingler l'atterrissage") : p("Libérer l'atterrissage")}
                           aria-pressed={!atterrissageLibre}
                           onClick={() => { setAtterrissageLibre(!atterrissageLibre); setAtterrissageVu(false); }}>
                     {atterrissageLibre ? <PinOff size={15} strokeWidth={1.7} aria-hidden="true" />
                       : <Pin size={15} strokeWidth={1.7} aria-hidden="true" />}
                   </button>
                 )}>
        <div className="om-eq">
          <div className="om-eq__r"><span>{finClos ? `${p('CA réalisé à fin')} ${finClos}` : p('CA réalisé')}</span><span>{val(t('reel') || null)}</span></div>
          <div className="om-eq__r"><span>+ {p('CA du portefeuille')}</span><span>{val(t('bk') || null)}</span></div>
          <div className="om-eq__r"><span>+ {p('CA des nouvelles ventes')}</span><span>{val(t('neuf') || null)}</span></div>
          <div className="om-eq__r om-eq__r--fort"><span>= {p('Atterrissage')}</span><span>{val(att || null)}</span></div>
          <div className="om-eq__r om-eq__r--cible"><span>{p('Cible')} · {nomCible}</span><span>{cible == null ? NA : val(cible || null)}</span></div>
          <div className="om-eq__r"><span>{p('Écart à la cible')}</span><b>{cible == null ? NA : ecartCHF(att - cible)}</b></div>
        </div>
        <div className="om-eq__barre" aria-hidden="true">
          <i className="om-eq__b--reel" style={{ width: part(t('reel')) }} />
          <i className="om-eq__b--bk" style={{ width: part(t('bk')) }} />
          <i className="om-eq__b--nv" style={{ width: part(t('neuf')) }} />
        </div>
        <table className="om-eq__offres">
          <thead><tr><th className="om-lbl">{p('Offre')}</th><th>{p('Atterrissage')}</th><th>{p('Cible')}</th><th>{p('Écart')}</th></tr></thead>
          <tbody>
            {vs.map(({ r, v }) => (
              <tr key={r.offer_id}>
                <td className="om-lbl">{r.name}</td><td>{val(v.att || null)}</td>
                <td>{v.cible == null ? NA : val(v.cible || null)}</td><td>{v.cible == null ? NA : ecartCHF(v.att - v.cible)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardShell>
    );
    if (!atterrissageLibre) return carte;
    return (
      <div className="om-flottant" onMouseLeave={() => setAtterrissageVu(false)}>
        <button type="button" className="om-languette" title={p("Ouvrir l'atterrissage")}
                onMouseEnter={() => setAtterrissageVu(true)} onClick={() => setAtterrissageVu(!atterrissageVu)}>
          <span className="om-languette__t">{p('Atterrissage')}</span>
          {cible != null && <span className="om-languette__e">{ecartCHF(att - cible)}</span>}
        </button>
        {carte}
      </div>
    );
  })() : null;
  return (
    <PageShell framed title={titre} exportRef={exportRef} unit={pageUnit} views={vuesPage}>
      {profilsOuverts ? (
        <CardShell title={p('Profils de saisonnalité')} flush
                   primaryAction={(
                     <>
                       <button type="button" className="lk-btn lk-btn--quiet" onClick={() => setProfilsOuverts(false)}>
                         {p('Fermer les profils')} ✕
                       </button>
                       {canWrite && (<>
                       {/* CHARGER LES PROFILS DE BASE (demande d'Olivier, 2026-10-11) :
                           ceux qui manquent s'ajoutent, les autres ne bougent pas. */}
                       <button type="button" className="lk-btn lk-btn--quiet" onClick={async () => {
                         const r = await loadStandardProfiles(tid);
                         if (r && r.error) { addToast && addToast(r.error, 'error'); return; }
                         if (r && r.data && r.data.added === 0) addToast && addToast(p('Les profils de base sont déjà tous chargés.'));
                         await reload();
                       }}>
                         {p('Charger les profils de base')}
                       </button>
                       <button type="button" className="lk-btn" onClick={() => profilAppel(createSeasonProfile(tid, { weights: Array(12).fill(100) }))}>
                         ＋ {p('Profil')}
                       </button>
                       </>)}
                     </>
                   )}>
          <SeasonProfilesPanel profils={profils} canWrite={canWrite}
                               onUpdate={(id, data) => profilAppel(updateSeasonProfile(tid, id, data))}
                               onDelete={(id) => profilAppel(deleteSeasonProfile(tid, id))}
                               onMove={(id, sens) => profilAppel(updateSeasonProfile(tid, id, { move: sens }))} />
        </CardShell>
      ) : ecran === 'ca' ? (
        /* Le CHIFFRE D'AFFAIRES : à l'année, où le CA réalisé se tape, ou par mois. */
        <>
        <CardShell title={`${p("Chiffre d'affaires")} ${an0} ${p('par offre')} · ${dev}`} flush className={vide ? '' : 'om-card-fit'}
                   primaryAction={onGoRealise && vue && !vide ? (
                     <button type="button" className="lk-btn lk-btn--toggle" onClick={onGoRealise}>
                       <RiArrowRightUpLine aria-hidden="true" />{p('Saisir le réalisé')}
                     </button>
                   ) : null}
                   viewControls={vue && !vide ? (
                     <SegmentedToggle value={caVue} onChange={setCaVue}
                                      options={[{ value: 'an', label: p('Année') }, { value: 'mois', label: p('Mois') }]} />
                   ) : null}>
          {vide ? sansOffre : caVue === 'mois' ? tableCAMois : tableCAAn}
        </CardShell>
        </>
      ) : (
        <>
          {/* Les VENTES et leurs mois, une seule carte ; le MODE DE SAISIE se
              pose au-dessus (demande d'Olivier, 2026-10-11). */}
          <div className={`om-grille${atterrissageLibre ? ' om-grille--libre' : ''}`}>
          <div className="om-grille__g">
          {/* « Modifier les profils » : une ACTION de la carte, plus une vue de la
              page (2026-10-16). Contour discret, texte ardoise, petit. */}
          <CardShell title={`${p('Ventes')} ${an0}`} flush
                     primaryAction={vue && !vide ? (
                       <>
                         {/* AJOUTER UNE LIGNE, EN CHOISISSANT L'OFFRE (demande d'Olivier,
                             2026-10-20) : le bouton de la carte propose les offres, la
                             ligne naît sous celle qu'on choisit. */}
                         {canWrite && offresAjout.length > 0 && (
                           <Listbox className="om-addligne" value="" placeholder={`＋ ${p('Ligne')}`}
                                    options={offresAjout.map((r) => ({ value: r.offer_id, label: r.name }))}
                                    onChange={(id) => { const r = offresAjout.find((x) => x.offer_id === id); if (r) ajouterLigne(r); }} />
                         )}
                         <button type="button" className="lk-btn lk-btn--toggle" onClick={() => setProfilsOuverts(true)}>
                           <RiEqualizerLine aria-hidden="true" />{p('Modifier les profils')}
                         </button>
                       </>
                     ) : null}
                     viewControls={vue && !vide && axe === 'offre' ? (
                       <button type="button" className="lk-btn lk-btn--toggle om-toutplier" onClick={basculerTout}
                               aria-pressed={toutOuvert}>
                         {toutOuvert ? <RiCollapseVerticalLine aria-hidden="true" /> : <RiExpandVerticalLine aria-hidden="true" />}
                         {toutOuvert ? p('Tout replier') : p('Tout déplier')}
                       </button>
                     ) : null}
                     /* LA RECHERCHE VIT DANS LA BARRE DE FILTRES (demande d'Olivier,
                        2026-10-20), sous l'entête, ouverte par l'entonnoir : le
                        standard de carte, comme dans People. */
                     /* LES OFFRES EN PASTILLES (demande d'Olivier, 2026-10-20) : un clic
                        filtre sur l'offre, plusieurs pastilles se cumulent ; elles
                        écrivent les mêmes jetons que la recherche (« o:<offre> »), qui
                        garde les lignes. */
                     filters={vue && !vide ? (
                       <PillFilter label={p('Offre')} value={filtreRech} onChange={setFiltreRech}
                                   options={vue.rows.filter((r) => r.lines.length > 0 && r.equation !== 'base_rate')
                                     .map((r) => ({ value: `o:${r.offer_id}`, label: r.name }))} />
                     ) : null}
                     search={vue && !vide ? (
                       <FilterSearch items={itemsRech} placeholder={p('Offre ou ligne…')}
                                     /* Les offres retenues se lisent sur leurs pastilles : la recherche
                                        ne montre que les lignes choisies (2026-10-20). */
                                     selected={new Set([...filtreRech].filter((k) => k.startsWith('l:')))}
                                     onChange={(s) => setFiltreRech(new Set([...[...filtreRech].filter((k) => k.startsWith('o:')), ...s]))} />
                     ) : null}
                     filterCount={filtreRech.size} collapsible storageKey="om-ventes">
            {vide ? sansOffre : tableVentes}
          </CardShell>
          </div>
          {panneauCA}
          </div>
        </>
      )}
      {edition && <OfferCreateDialog modeles={edition.modeles} lines={edition.lines} offre={edition.offre}
                                     onCreate={modifierOffre} onClose={() => setEdition(null)} />}
    </PageShell>
  );
}

